import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { Container } from 'theme-ui';

import { useInitSelectedVariant, usePickedSections, useRebuy } from '@hooks';
import { Breadcrumbs, ProductReviews } from '@snippets';
import store, { useSetRecoilState } from '@store';
import { getValidId } from '@utils';
import {
  useCart,
  useCartCount,
  useCollectionByHandle,
} from '@backpackjs/storefront';

import { Main } from './Main';
import { useProductIntelliSuggest } from './useProductIntelliSuggest';

function handlelize(str) {
  const handle = str
    .normalize('NFD')
    .replace(/[\[\]'()"]+/g, '') // Remove apostrophes, square brackets, and other bits and pieces
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/([^\w]+|\s+)/g, '-') // Replace space and other characters by hyphen
    .replace(/\-\-+/g, '-') // Replaces multiple hyphens by one hyphen
    .replace(/(^-+|-+$)/g, '') // Remove extra hyphens from beginning or end of the string
    .toLowerCase(); // To lowercase

  return handle;
}

export function ProductComponent({ product, sections }) {
  const ref = useRef();

  const { anyOf, otherSections } = usePickedSections({
    anyOf: ['featuredTwoTile', 'productUpsells', 'videoWise'],
    sections,
  });

  const selectedVariant = useInitSelectedVariant();
  const cart = useCart();
  const cart_item_count = useCartCount();

  const hasMysteryItem = useMemo(() => {
    return cart?.lines?.some(
      (line) => line?.variant?.product?.handle === 'mystery-item'
    );
  }, [cart?.updatedAt]);

  const collectionHandle = useMemo(() => {
    let title = product?.title;
    if (product?.title?.indexOf('|') !== -1) {
      title = product?.title?.split('|')?.[0]?.trim() || '';
    }
    const pHandle = handlelize(title);
    const collection = product?.collections?.find((c) => {
      return pHandle?.includes(c?.handle);
    });
    if (collection?.handle) {
      return collection.handle;
    }
    return product?.collections?.[0]?.handle;
  }, [product?.title, product?.collections]);

  const { collection } = useCollectionByHandle({
    handle: collectionHandle,
  });

  // pass the mystery-item to rebuy if it's already in the cart
  const cartProductIds = useMemo(() => {
    return (
      cart?.lines?.map((line) => {
        if (line?.variant?.product?.handle === 'mystery-item') {
          const productId = getValidId(line.variant.product?.id);
          return productId || null;
        }
        return null;
      }) || []
    );
  }, [cart?.updatedAt]);

  const product_ids =
    selectedVariant?.product?.legacyResourceId ||
    product?.variants?.[0]?.product?.legacyResourceId;

  const shopify_product_ids = [product_ids, ...cartProductIds].join(',');

  const [rebuyProducts, setRebuyProducts] = useState(null);
  const { getRebuyProducts } = useRebuy();

  useEffect(() => {
    const handleRebuy = async () => {
      const rebuyProductsData = await getRebuyProducts({
        shopify_product_ids,
        cart_item_count,
      });

      // filter out mystery item if it's already in the cart
      let crossSellProducts = rebuyProductsData?.crossSells?.data;
      let upSellProducts = rebuyProductsData?.upSells?.data;
      if (hasMysteryItem) {
        crossSellProducts = crossSellProducts?.filter(
          (_product) => _product?.handle !== 'mystery-item'
        );

        upSellProducts = upSellProducts?.filter(
          (_product) => _product?.handle !== 'mystery-item'
        );
      }

      setRebuyProducts({
        crossSells: {
          products: crossSellProducts,
          language: rebuyProductsData?.crossSells?.metadata?.widget?.language,
        },
        upSells: {
          products: upSellProducts,
          language: rebuyProductsData?.upSells?.metadata?.widget?.language,
        },
      });
    };

    if (shopify_product_ids) handleRebuy();
  }, [shopify_product_ids, hasMysteryItem]);

  // send intellisuggest event on product view
  useProductIntelliSuggest({ sku: selectedVariant?.sku });

  const setInitialProduct = useSetRecoilState(store.initialProduct);

  const id = product?.legacyResourceId || '';

  const updateLastViewedOnProductPage = useCallback(() => {
    const lastViewed = window.localStorage.getItem('lastViewed');
    if (lastViewed) {
      const lastViewedIds = lastViewed.split(',');

      if (!lastViewedIds.includes(id)) {
        window.localStorage.setItem(
          'lastViewed',
          `${id},${lastViewedIds.slice(0, 4).join(',')}`
        );
      }
    } else {
      window.localStorage.setItem('lastViewed', id);
    }
  }, [id]);

  useEffect(() => {
    updateLastViewedOnProductPage();
  }, [id]);

  useEffect(() => {
    setInitialProduct(product);
  }, [id]);

  return (
    <Container data-comp={ProductComponent.displayName} ref={ref}>
      <Breadcrumbs
        selectedVariant={selectedVariant}
        isPdp
        collection={collection}
      />
      <Main
        product={product}
        selectedVariant={selectedVariant}
        productUpsells={anyOf.productUpsells}
        rebuyProducts={rebuyProducts}
        videoWise={anyOf?.videoWise}
      />

      {otherSections || null}

      <ProductReviews />

      {anyOf.featuredTwoTile || null}
    </Container>
  );
}

ProductComponent.displayName = 'ProductComponent';
